/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Container, Divider, Drawer, IconButton, Stack,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AddCircleOutlineRounded, LocationOnOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Images from '../../../../Configs/images';
import AlertContext from '../../../../Context/AlertContext';
import Elements from '../../../Elements';
import { getAddress } from '../../../../Redux/Models/address';
import { getUserData } from '../../../../Utils/storages';
import ROUTES from '../../../../Configs/routes';
import { AddressBoxes } from '../AddressList/AddressList';
import { checkoutItems, getCart } from '../../../../Redux/Models/market';
import { formatNum, formatNumToCurrency } from '../../../../Utils/helpers';
import ConfirmationContext from '../../../../Context/ConfirmationContext';

export default function Component({
  classes, error, message, cart, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = getUserData();
  const { devId } = useParams();
  const { address } = useSelector((state) => state);
  const { t } = useTranslation();
  const [fields, setFields] = useState({
    pickup_address: {},
    pickup_date: new Date(),
  });
  const [openChangeAddress, setOpenChangeAddress] = useState(false);

  const { openConfirmation, closeConfirmation } = useContext(ConfirmationContext);
  const { openAlert } = useContext(AlertContext);

  const quota = userData.quota || 0;

  useEffect(() => {
    dispatch(getCart(userData.id));
    dispatch(getAddress(userData.id));
  }, []);

  useEffect(() => {
    if (address.data.length) {
      const selectedAddress = address.data.filter((addr) => addr.is_default)[0];
      setFields({
        ...fields,
        pickup_address: selectedAddress,
      });
    }
  }, [address]);

  useEffect(() => {
    if (error) {
      openAlert({
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const takeAddress = (add) => {
    setFields({ ...fields, pickup_address: add });
    setOpenChangeAddress(false);
  };

  const checkStorageIsEnough = () => {
    const itemIds = cart.map((item) => item.item?.id);
    if (quota < itemIds?.length || !quota) {
      openConfirmation({
        handleAgree: () => {
          navigate(ROUTES.extraStorage());
        },
        title: 'Running out of storage',
        desc: 'You need to get extra storage before buying for a product/item',
        confirmText: 'Extra Storage',
      });

      return false;
    }
    return true;
  };

  const handleCheckout = () => {
    const enoughStorage = checkStorageIsEnough();
    if (enoughStorage) {
      const itemIds = cart.map((item) => item.item?.id);
      dispatch(checkoutItems(userData.id, { items: itemIds }));
    }
  };

  const renderAddress = () => (
    <Box mt={1} p={1.5} border="1px solid #E5E5E5" borderRadius={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <div className="weight-700">{fields.pickup_address?.label}</div>
        <div className="weight-600 primary-green link" onClick={() => setOpenChangeAddress(true)}>{t('common.change')}</div>
      </Box>
      <Box mt={1}>
        <div className="s-12">{fields.pickup_address?.name}</div>
        <div className="s-12 mt-1">
          +
          {fields.pickup_address?.phone_number}
        </div>
      </Box>
      <Box mt={0.5} bgcolor="#F5F6F8" borderRadius={1} py={0.5} px={1} display="flex" alignItems="flex-start" gap={1}>
        <LocationOnOutlined fontSize="small" />
        <div className="s-12">
          {fields.pickup_address.address}
          ,
          {' '}
          {fields.pickup_address.city}
          ,
          {' '}
          {fields.pickup_address.province}
          ,
          {' '}
          {fields.pickup_address.country}
          ,
          {' '}
          {fields.pickup_address.postal_code}
        </div>
      </Box>
    </Box>
  );

  const totalPrice = cart.reduce((acc, item) => acc + item.item.price || 0, 0);

  return (
    <Elements.PageBase navigate={navigate} noNav>
      <Box className={classes.root}>
        <Elements.Navigator title={t('cart.title2')} />
        <Container>
          <Box className="s-14 weight-700" mt={2}>{t('cart.address')}</Box>
          {renderAddress()}
        </Container>

        <Divider sx={{ border: '4px solid #F5F6F8', my: 2 }} />

        <Container>
          {isLoading && (
            <Box textAlign="center">
              {' '}
              <CircularProgress sx={{ color: 'black' }} />
              {' '}
            </Box>
          )}
          {cart.map((item, idx) => (
            <Box key={item.id}>
              <Box className="s-12 weight-700">
                {t('common.item')}
                {' '}
                {idx + 1}
              </Box>
              <Box display="flex" gap={1.5} mt={1} mb={2}>
                <img
                  width="80px"
                  height="80px"
                  className="rounded cover"
                  src={item.item?.photo || item.box?.photo}
                  alt="baco"
                />
                <Box flex={1}>
                  <Box className="weight-600">{item.item?.name}</Box>
                  <Box display="flex" className="s-10 mute" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <div>{item.category_name || '-'}</div>
                    <div>
                      ・
                      {item.condition_name || '-'}
                    </div>
                    <div>
                      ・
                      {item.volume || '0 mL'}
                    </div>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Box className="s-16 weight-700">
                      {formatNumToCurrency(Number(item.item?.price))}
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Box>
                        {t('marketPage.qty')}
                        {' '}
                        1
                      </Box>
                      <img src={Images.CheckRed} alt="check-icon" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Container>

        <Divider sx={{ border: '4px solid #F5F6F8', my: 2 }} />

        <Container>
          <Box className="weight-700">{t('cart.purchase')}</Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Box className="s-12 weight-400">{t('cart.price')}</Box>
            <Box className="s-12 weight-700">
              ₣
              {' '}
              {totalPrice}
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Box className="s-12 weight-400">{t('cart.shipCharge')}</Box>
            <Box className="s-12 weight-700">FREE</Box>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Box className="s-12 weight-400">{t('cart.cargoCharge')}</Box>
            <Box className="s-12 weight-700">FREE</Box>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Box className="s-12 weight-400">{t('cart.tax')}</Box>
            <Box className="s-12 weight-700">FREE</Box>
          </Box>

          <Divider sx={{ border: '1px dashed #F5F6F8', my: 1.5 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box className="s-12 weight-700">{t('cart.totalPay')}</Box>
            <Box className="s-12 weight-700 primary-green">
              {formatNumToCurrency(totalPrice)}
            </Box>
          </Box>
        </Container>

        <Divider sx={{ border: '4px solid #F5F6F8', my: 2 }} />

        <Container>
          <Box className="s-12 weight-400">
            {t('common.torConfirm')}
            {' '}
            <Link to={ROUTES.termsConditions()} className="link weight-700 black">{t('common.tor')}</Link>
            {' '}
            <Link to={ROUTES.privacyPolicy()} className="link weight-700 black">{t('common.privacy')}</Link>
            {' '}
            of OenoBank.
          </Box>
        </Container>

        <Box
          border="none"
          sx={{ outline: 'none' }}
          boxShadow="none"
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          py={1}
          px={1.5}
          // bgcolor="white"
        >
          <Button variant="contained" className="btn-contained" fullWidth onClick={handleCheckout}>
            {t('common.order')}
          </Button>
        </Box>

      </Box>

      <Drawer
        anchor="right"
        open={openChangeAddress}
        onClose={() => setOpenChangeAddress(false)}
      >
        <Elements.Navigator
          title={t('address.changeAdd')}
          onBack={() => setOpenChangeAddress(false)}
          secondaryBtn={(
            <IconButton LinkComponent={Link} to={`${ROUTES.address()}/update`}>
              <AddCircleOutlineRounded fontSize="small" className="black" />
            </IconButton>
          )}
        />
        <Box maxWidth="375px" px={2} boxSizing="border-box">
          <AddressBoxes selectAddress={takeAddress} />
        </Box>
      </Drawer>
    </Elements.PageBase>
  );
}
